import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { JobsChartSegments, SortTypes } from "../../../../core/Enums";
import Modal from "../../../../components/base/Modal";
import { TagifyDataItem } from "../../../../models/TagifyOptions";
import { openJobQuickView, openReportJobsList, setJobsReportBody, setPage, setToolbarActions, toPrettyDate, useEnvironment, useHub, useJobsReportBody } from "../../../../core/Global";
import Avatar from "../../../../components/base/Avatar";
import Indicator from "../../../../components/base/Indicator";
import Div from "../../../../components/base/Div";
import { FontAwesomeIcons } from "../../../../core/Types";
import { API_ROUTES } from "../../../../core/Constants";
import Filtering from "../../../../components/data/Filtering";
import FilteringPropsAgents from "../../../crm/jobs/filtering/filterings-agents.json";
import FilteringPropsTechs from "../../../crm/jobs/filtering/filterings-techs.json";
import FilteringPropsSources from "../../../crm/jobs/filtering/filterings-sources.json";
import FilteringPropsTypes from "../../../crm/jobs/filtering/filterings-types.json";
import FilteringPropsMetros from "../../../crm/jobs/filtering/filterings-metros.json";
import FilteringPropsPayments from "../../../crm/jobs/filtering/filterings-payments.json";
import FilteringPropsPaymentsDetails from "../../../crm/jobs/filtering/filterings-payments-details.json";
import FilteringPropsClosedJobs from "../../../crm/jobs/filtering/filterings-closedjobs.json";
import DataItem from "../../../../models/DataItem";
import JobReportModel, { DataColumnsVisibility, JobCancelReasonShare, JobReportDataRow, JobReportTechPaymentRow, JobsColumnsVisibility, TechPayColumnsVisibility } from "../models/JobReportModel";
import { JobsReportSort, dateTypeName, effectiveDateType, timezoneLabel } from "../models/JobsReportRequest";
import JobReportSendDrawer from "../components/JobReportSendDrawer";
import JobsReportColumnsDrawer from "../components/JobsReportColumnsDrawer";
import Button from "../../../../components/base/Button";
import { MenuItem } from "../../../../components/menu/DropdownMenu";
import Job from "../../../crm/jobs/models/Job";
import JobReportIssue from "../components/JobReportIssue";
import JobPaymentDetails from "../../../crm/jobs/models/JobPaymentDetails";
import JobSettleMenu from "../components/JobSettleMenu";
import BalanceSheetView from "./BalanceSheetView";
import CancelReasonsCard from "../components/CancelReasonsCard";



export default function JobsReport() {

    const moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    });

    const moneyFormatterNoneSign = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: "code"
    });

    let searchTimout: any;
    const env = useEnvironment()
    const hub = useHub()
    const body = useJobsReportBody()

    // The settings drawer sends a PATCH of the fields it owns, merged here onto whatever the
    // body is at that moment. Read through a ref so the merge can never be based on a body
    // captured by an older render.
    const bodyRef = useRef(body)
    bodyRef.current = body

    const [bodyFetched, bodyFetchedSet] = useState<boolean>(false)
    useEffect(() => {
        if (body) bodyFetchedSet(true)
    }, [body])

    const searchParams = useSearchParams()

    const navigate = useNavigate()
    const { segment } = useParams()

    const segmentTitle = (s?: JobsChartSegments) => {
        if (!body) return "";
        if ((s ?? body.segment) == JobsChartSegments.Dispatchers) return "Dispatcher";
        else if ((s ?? body.segment) == JobsChartSegments.JobSource) return "Job Source";
        else if ((s ?? body.segment) == JobsChartSegments.ClosedJobs) return "Closed Jobs";
        else if ((s ?? body.segment) == JobsChartSegments.CanceledJobs) return "Canceled Jobs";
        else if ((s ?? body.segment) == JobsChartSegments.JobType) return "Job Type";
        else if ((s ?? body.segment) == JobsChartSegments.Metro) return "Metro";
        else if ((s ?? body.segment) == JobsChartSegments.Payments) return "Payment";
        else if ((s ?? body.segment) == JobsChartSegments.Technician) return "Technician";
        return "";
    }

    const segmentIcon = (s?: JobsChartSegments): FontAwesomeIcons => {
        if (!body) return "dot";
        if ((s ?? body.segment) == JobsChartSegments.Dispatchers) return "user-headset";
        else if ((s ?? body.segment) == JobsChartSegments.JobSource) return "bullhorn";
        else if ((s ?? body.segment) == JobsChartSegments.ClosedJobs) return "circle";
        else if ((s ?? body.segment) == JobsChartSegments.CanceledJobs) return "circle";
        else if ((s ?? body.segment) == JobsChartSegments.JobType) return "wrench";
        else if ((s ?? body.segment) == JobsChartSegments.Metro) return "map";
        else if ((s ?? body.segment) == JobsChartSegments.Payments) return "credit-card";
        else if ((s ?? body.segment) == JobsChartSegments.Technician) return "user-helmet-safety";
        return "dot";
    }

    const [SegmentsMenuSource, SegmentsMenuSourceSet] = useState<MenuItem[]>([])
    useEffect(() => {

        var src: MenuItem[] = [];

        src.push({
            id: JobsChartSegments.Dispatchers.toString(),
            title: "Dispatcher",
            icon: "user-headset",
            action() {
                updateSegment(JobsChartSegments.Dispatchers)
            },
        })

        src.push({
            id: JobsChartSegments.Technician.toString(),
            title: "Technician",
            icon: "user-helmet-safety",
            action() {
                updateSegment(JobsChartSegments.Technician)
            },
        })

        src.push({
            id: JobsChartSegments.JobSource.toString(),
            title: "Job Source",
            icon: "bullhorn",
            action() {
                updateSegment(JobsChartSegments.JobSource)
            },
        })

        src.push({
            id: JobsChartSegments.JobType.toString(),
            title: "Job Type",
            icon: "wrench",
            action() {
                updateSegment(JobsChartSegments.JobType)
            },
        })

        src.push({
            id: JobsChartSegments.Metro.toString(),
            title: "Metro",
            icon: "map",
            action() {
                updateSegment(JobsChartSegments.Metro)
            },
        })

        src.push({
            id: JobsChartSegments.Payments.toString(),
            title: "Payments",
            icon: "credit-card",
            action() {
                updateSegment(JobsChartSegments.Payments)
            },
        })

        src.push({
            id: JobsChartSegments.ClosedJobs.toString(),
            title: "Closed Jobs",
            icon: "circle",
            action() {
                updateSegment(JobsChartSegments.ClosedJobs)
            },
        })

        src.push({
            id: JobsChartSegments.CanceledJobs.toString(),
            title: "Canceled Jobs",
            icon: "circle",
            action() {
                updateSegment(JobsChartSegments.CanceledJobs)
            },
        })

        SegmentsMenuSourceSet(src)

    }, [bodyFetched])

    const initialSegment = () => {

        if (segment) {
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.Dispatchers].toLowerCase()) return JobsChartSegments.Dispatchers;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.Technician].toLowerCase()) return JobsChartSegments.Technician;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.ClosedJobs].toLowerCase()) return JobsChartSegments.ClosedJobs;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.JobSource].toLowerCase()) return JobsChartSegments.JobSource;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.JobType].toLowerCase()) return JobsChartSegments.JobType;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.Metro].toLowerCase()) return JobsChartSegments.Metro;
            //if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.Flags].toLowerCase()) return JobsChartSegments.Flags;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.Payments].toLowerCase()) return JobsChartSegments.Payments;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.ClosedJobs].toLowerCase()) return JobsChartSegments.ClosedJobs;
            if (segment.toLowerCase() == JobsChartSegments[JobsChartSegments.CanceledJobs].toLowerCase()) return JobsChartSegments.CanceledJobs;
        }
        var seg = parseInt(localStorage.getItem("jobs_reports_segment") ?? "1");
        if (seg == 1) return JobsChartSegments.Dispatchers;
        if (seg == 2) return JobsChartSegments.Technician;
        if (seg == 3) return JobsChartSegments.ClosedJobs;
        if (seg == 4) return JobsChartSegments.JobSource;
        if (seg == 5) return JobsChartSegments.JobType;
        if (seg == 6) return JobsChartSegments.Metro;
        //if (seg == 7) return JobsChartSegments.Flags;
        if (seg == 8) return JobsChartSegments.Payments;
        if (seg == 9) return JobsChartSegments.ClosedJobs;
        if (seg == 10) return JobsChartSegments.CanceledJobs;

        return JobsChartSegments.Payments;
    }

    const initialDateId = () => {
        var d = localStorage.getItem("jobs_reports_dateid") ?? "1";
        return d;
    }

    const initialSortKey = () => {
        return "";
    }

    const initialSortType = () => {
        return SortTypes.Descending;
    }

    const [timezonesSource, timezonesSourceSet] = useState<any[]>([])

    const [selectedTimeZone, selectedTimeZoneSet] = useState<any>();



    useEffect(() => {
        // Cleaned once here, so the settings dropdown and the toolbar line (which reads the
        // selected item straight out of this list) both show the tidy label.
        hub.Get("/reports/jobs/timezones", res => timezonesSourceSet(
            (res.data ?? []).map((t: any) => ({ ...t, name: timezoneLabel(t.name) }))
        ))
    }, [])

    useEffect(() => {
        let tz: any;

        timezonesSource.forEach(t => {
            if (t.id == body?.timezone) {
                tz = t;
            }
        })

        // A saved id that is no longer in the list (ids differ between platforms and have
        // changed shape before) must NOT fall back to "Job Timezone": the report really is
        // being run in that timezone, and saying otherwise here is worse than an odd label.
        // Show the raw id so the line stays truthful.
        if (!tz && body?.timezone && body.timezone != "job") {
            tz = { id: body.timezone, name: body.timezone };
        }

        if (!tz) {
            timezonesSource.forEach(t => {
                if (t.id == "job") {
                    tz = t;
                }
            })
        }

        selectedTimeZoneSet(tz);

    }, [timezonesSource, body])



    const [filterQuery, filterQuerySet] = useState<string>()


    const [sortBody, sortBodySet] = useState<JobsReportSort>({
        sortKey: initialSortKey(),
        sortType: initialSortType()
    });

    const [dates, datesSet] = useState<DataItem[]>([])
    useEffect(() => {
        hub.Get("/reports/jobs/dates", res => datesSet(res.data))
    }, [])

    const ActionButtons = () => {
        return (
            <FlexRow>
                <Button type="light" color="primary"
                    icon="refresh" text="Refresh"
                    className="btn-active-light-primary"
                    onClick={fetch} />
            </FlexRow>
        )
    }




    const [model, modelSet] = useState<JobReportModel>();
    const [allJobs, jobsSet] = useState<Job[]>();

    // Canceled Jobs: the reasons picked on the Cancel Reasons card. The table (and its count and
    // totals row) shows only the jobs under them - filtered here, client side, from the job ids
    // the HUB sends with each reason.
    const [cancelReasonKeys, cancelReasonKeysSet] = useState<string[]>([]);
    const jobs = useMemo(() => {
        if (!allJobs || cancelReasonKeys.length == 0 || body?.segment != JobsChartSegments.CanceledJobs) return allJobs;
        const ids = new Set<string>();
        const collect = (items?: JobCancelReasonShare[]) => items?.forEach(item => {
            if (cancelReasonKeys.includes(item.key)) item.jobIds.forEach(id => ids.add(id));
            collect(item.items);
        });
        collect(model?.cancelReasons);
        return allJobs.filter(j => ids.has(j.id!));
    }, [allJobs, cancelReasonKeys, model, body?.segment]);
    const [jobsTotalRow, jobsTotalRowSet] = useState<JobPaymentDetails>();
    const [data, dataSet] = useState<JobReportDataRow[]>();
    const [avTickets, avTicketsSet] = useState<number>();
    const [payments, paymentsSet] = useState<JobReportTechPaymentRow[]>();
    const [loading, loadingSet] = useState<boolean>(true);
    const [loaded, loadedSet] = useState<boolean>(false);
    const [firstLoad, firstLoadSet] = useState<boolean>(true);
    const [filteringValues, filteringValuesSet] = useState<any>();

    useEffect(() => {

        if (model && !loading) {
            var pieNumbers: number[] = [];
            var pieColors: string[] = [];
            model.pieChart?.statusCounts.forEach(s => {
                if (s.count > 0 && s.color) {
                    pieNumbers.push(s.count);
                    pieColors.push(s.color);
                }

            });

            window.createCircleChart("jobchart-circle", pieNumbers, pieColors);
            // window.createJobReportsSampleChart("kt_card_widget_1_chart");
            // window.createJobReportsSampleChart("kt_card_widget_2_chart");
            // window.createJobReportsSampleChart("kt_card_widget_3_chart");

            // $("#chart-target").html("");
            // $("#chart-target").html("<div id='main-chart' style='height: 400px'></div>");

            // setTimeout(() => {
            //     if(model.chart) {
            //         var chart = window.createJobsChart(model.chart);    
            //     }
            // }, 500);

            jobsSet(model.jobs);
            cancelReasonKeysSet([]);
            dataSet(model.data);
            paymentsSet(model.techPayments);

            if (model.data) {
                for (var i = 0; i < model.data.length; i++) {
                    if (model.data[i].id == "total") {
                        avTicketsSet(model.data[i].averageTickets);
                        break;
                    }
                }
            }
            //chartSet(chart);
        }

    }, [model, loading])

    useEffect(() => {
        if (jobs && jobs.length > 0) {

            var total: JobPaymentDetails = {
                amount: 0,
                totalCash: 0,
                totalCCCharge: 0,
                totalCCManual: 0,
                totalCCChargeRate: 0,
                totalCheck: 0, totalCheckRate: 0,
                totalEPay: 0,
                totalRinvoxPay: 0, totalRinvoxFee: 0,
                companyParts: 0,
                techParts: 0,
                companyProfit: 0,
                totalTaxRate: 0,
                techProfit: 0,
                sourceProfit: 0,
                techBalance: 0,
                techFee: 0
            };

            jobs.forEach(j => {
                total.amount! += (j.payment?.totalPayment ?? 0);
                total.totalCash! += (j.paymentDetails?.totalCash ?? 0);
                total.totalCCCharge! += (j.paymentDetails?.totalCCCharge ?? 0);
                total.totalCCManual! += (j.paymentDetails?.totalCCManual ?? 0);
                total.totalCheck! += (j.paymentDetails?.totalCheck ?? 0);
                total.totalCheckRate! += (j.paymentDetails?.totalCheckRate ?? 0);
                total.totalEPay! += (j.paymentDetails?.totalEPay ?? 0);
                total.totalRinvoxPay! += (j.paymentDetails?.totalRinvoxPay ?? 0);
                total.totalRinvoxFee! += (j.paymentDetails?.totalRinvoxFee ?? 0);
                total.companyParts! += (j.paymentDetails?.companyParts ?? 0);
                total.techParts! += (j.paymentDetails?.techParts ?? 0);
                total.companyProfit! += (j.paymentDetails?.companyProfit ?? 0);
                total.totalTaxRate! += (j.paymentDetails?.totalTaxRate ?? 0);
                total.techProfit! += (j.paymentDetails?.techProfit ?? 0);
                total.sourceProfit! += (j.paymentDetails?.sourceProfit ?? 0);
                total.techBalance! += (j.paymentDetails?.techBalance ?? 0);
                total.totalCCChargeRate! += (j.paymentDetails?.totalCCChargeRate ?? 0);
                total.techFee! += (j.paymentDetails?.techFee ?? 0) + (j.paymentDetails?.sourceFee ?? 0);
            })
            jobsTotalRowSet(total);
        }
    }, [jobs, body])

    const fetch = () => {

        loadingSet(true);

        var q = "?a=1";

        if (firstLoad && searchParams && searchParams[0]) {
            q += "&" + searchParams[0].toString();
        }

        if (filterQuery) {
            q += "&" + filterQuery;
        }

        hub.Post("/reports/jobs/chart/" + q, {
            ...body,
            ...sortBody
        }, res => {
            if (res.success) {
                modelSet(res.data);
                filteringValuesSet(res.filteringValues);

                setTimeout(() => {
                    loadingSet(false);
                    loadedSet(true);
                    firstLoadSet(false);
                }, 500);
            }
        })
    }

    useEffect(() => {

        if (body?.segmentIds && body.segmentIds.length > 0) {
            fetchSegmentData("", undefined, body.segmentIds);
            segmentIdsSet(body.segmentIds);
        }

    }, [])

    const fetchSegmentData = (keyword?: string, include?: TagifyDataItem, ids?: string[]) => {
        if (!body) return;

        var excludeIds = "";
        for (var i = 0; i < segmentIds.length; i++) {
            excludeIds += "&excludeIds=" + segmentIds[i];
        }
        var includeIds = "";
        if (include) includeIds = "&includeIds=" + include.value;

        var route = "";
        var extra = "";
        if (body.segment == JobsChartSegments.Dispatchers) {
            route = "/base/tagify/users/";
            extra = "&type=2";
        }
        else if (body.segment == JobsChartSegments.Technician || body.segment == JobsChartSegments.Payments) {
            route = "/base/tagify/users/";
            extra = "&type=4";
        }
        else if (body.segment == JobsChartSegments.JobSource) route = "/base/tagify/jobsources/";
        else if (body.segment == JobsChartSegments.JobType) route = "/base/tagify/jobtypes/";
        //else if (body.segment == JobsChartSegments.Flags) route = "/base/tagify/flags/";

        // else if (body.segment == CallsChartSegments.Dispatchers) route = "/base/tagify/users/";
        // else if (body.segment == CallsChartSegments.Extensions) route = "/base/tagify/extensions/";
        // else if (body.segment == CallsChartSegments.Queues) route = "/base/tagify/queues/";




        hub.Get(route + "?keyword=" + keyword + extra + excludeIds + includeIds, res => {
            if (res.data) {
                segmentsDataSet(res.data);
                if (ids && ids.length > 0) {
                    var items = res.data.filter((f: any) => ids.includes(f.value));
                    selectedSegmentItemsSet(items);
                }
            }
        });
    }

    const fetchSelectedSegments = (segment: JobsChartSegments, ids: string[]) => {

        var includeIds = "";
        ids.forEach(id => includeIds = "&ids=" + id);

        var route = "";
        var extra = "";
        if (segment == JobsChartSegments.Dispatchers) {
            route = "/base/tagify/users/";
            extra = "&type=2";
        }
        else if (segment == JobsChartSegments.Technician || segment == JobsChartSegments.Payments) {
            route = "/base/tagify/users/";
            extra = "&type=4";
        }
        else if (segment == JobsChartSegments.JobSource) route = "/base/tagify/jobsources/";
        else if (segment == JobsChartSegments.JobType) route = "/base/tagify/jobtypes/";
        //else if (body.segment == JobsChartSegments.Flags) route = "/base/tagify/flags/";

        // else if (body.segment == CallsChartSegments.Dispatchers) route = "/base/tagify/users/";
        // else if (body.segment == CallsChartSegments.Extensions) route = "/base/tagify/extensions/";
        // else if (body.segment == CallsChartSegments.Queues) route = "/base/tagify/queues/";

        hub.Get(route + "?keyword=----" + extra + includeIds, res => {
            if (res.data) {
                selectedSegmentItemsSet(res.data)
            }
        });
    }



    useEffect(() => {


        fetch();

    }, [body, filterQuery])

    // Declared up here because the custom-range picker effect below reads it.
    const [columnsDrawer, columnsDrawerSet] = useState<boolean>(false)

    // The custom range picker lives inside the settings drawer now; it only reports the
    // chosen range back so these chart-timing flags stay current. The range itself reaches
    // the report through Apply.
    const customRangePicked = (start: any, end: any) => {
        var difDays = end.diff(start, "days");
        timingHourDisabledSet(difDays > 30);
        timingDayDisabledSet(difDays > 180 || difDays < 3);
        timingWeekDisabledSet(difDays < 14);
        timingMonthDisabledSet(difDays < 60);
    }

    useEffect(() => {
        console.log("reports body changed", body);

    }, [body, firstLoad])

    const [timingHourDisabled, timingHourDisabledSet] = useState<boolean>(false);
    const [timingDayDisabled, timingDayDisabledSet] = useState<boolean>(false);
    const [timingWeekDisabled, timingWeekDisabledSet] = useState<boolean>(false);
    const [timingMonthDisabled, timingMonthDisabledSet] = useState<boolean>(true);


    const [exportExcelWorking, exportExcelWorkingSet] = useState<boolean>(false);
    useEffect(() => {
        if (exportExcelWorking) exportExcelFile();
    }, [exportExcelWorking])

    const exportExcelFile = () => {

        exportExcelWorkingSet(true);

        var q = "?a=1";

        if (firstLoad && searchParams && searchParams[0]) {
            q += "&" + searchParams[0].toString();
        }

        if (filterQuery) {
            q += "&" + filterQuery;
        }

        hub.Post("/reports/jobs/export/" + q, {
            ...body,
            ...sortBody
        }, res => {
            exportExcelWorkingSet(false);
            if (res.success) {
                document.location.href = env.hubUrl + res.data;
            }
        })
    }


    const [boxHeight, boxHeightSet] = useState<number>();
    const [jobsTableHeight, jobsTableHeightSet] = useState<number>();
    const fixSizes = () => {
        var h = $(window).height() ?? 400;
        h = h - ($("#kt_header").height() ?? 0);
        h = h - ($("#kt_toolbar").height() ?? 0);
        h = h - ($("#job-reports-stickybar").outerHeight() ?? 0);
        h = h - 50;

        var jh = h - ($("#jobs-table-thead").height() ?? 0);

        boxHeightSet(h);
        jobsTableHeightSet(jh);
    }



    useEffect(() => {

        setPage("Jobs Report");
        setToolbarActions(ActionButtons);

        window.KTStickyCreate();
        fixSizes();

        window.addEventListener("resize", fixSizes);
        return () => {
            window.removeEventListener("resize", fixSizes);
            setToolbarActions(undefined);
        }

    }, [])


    useEffect(() => {
        setToolbarActions(ActionButtons);
    }, [body, filterQuery])



    useEffect(() => {
        if (sortBody.sortLoading) resort();
    }, [sortBody])

    const resort = () => {
        var q = "?a=1";

        if (firstLoad && searchParams && searchParams[0]) {
            q += "&" + searchParams[0].toString();
        }

        if (filterQuery) {
            q += "&" + filterQuery;
        }

        hub.Post("/reports/jobs/chart/" + q, {
            ...body,
            ...sortBody
        }, res => {
            if (res.success) {
                //modelSet(res.data);
                jobsSet(j => [...res.data.jobs]);
                dataSet(d => [...res.data.data]);
                paymentsSet(p => [...res.data.techPayments]);
                sortBodySet({
                    ...sortBody,
                    sortLoading: false
                });
            }
        })
    }

    const [exportTechId, exportTechIdSet] = useState<string>()

    useEffect(() => {
        if (!jobs || jobs.length == 0) {
            exportTechIdSet(undefined);
            return;
        }
        var techId = jobs[0].techId;
        if (jobs.every(f => f.techId == techId)) {
            exportTechIdSet(techId);
            return;
        }

        exportTechIdSet(undefined);
    }, [jobs])


    const [segmentIds, segmentIdsSet] = useState<string[]>([]);
    const [defaultSegmentId, defaultSegmentIdSet] = useState<string>();
    const [segmentsData, segmentsDataSet] = useState<TagifyDataItem[]>([]);
    const [selectedSegmentItem, selectedSegmentItemSet] = useState<TagifyDataItem>();
    const [selectedSegmentItems, selectedSegmentItemsSet] = useState<TagifyDataItem[]>([]);

    const [addSegment, addSegmentSet] = useState<boolean>(false);
    const [clearFilters, clearFiltersSet] = useState<boolean>(false);
    useEffect(() => {
        if (clearFilters) {
            setTimeout(() => {
                clearFiltersSet(false);
            }, 1000);
        }
    }, [clearFilters])

    const segmentToSlug = (s: JobsChartSegments): string => {
        switch (s) {
            case JobsChartSegments.Dispatchers: return "dispatcher";
            case JobsChartSegments.Technician: return "technician";
            case JobsChartSegments.JobSource: return "source";
            case JobsChartSegments.JobType: return "type";
            case JobsChartSegments.Metro: return "metro";
            case JobsChartSegments.Payments: return "payments";
            case JobsChartSegments.ClosedJobs: return "closed";
            case JobsChartSegments.CanceledJobs: return "canceled";
            default: return "dispatcher";
        }
    }

    const slugToSegment = (s?: string): JobsChartSegments | undefined => {
        if (!s) return undefined;
        switch (s.toLowerCase()) {
            case "dispatcher": return JobsChartSegments.Dispatchers;
            case "technician": return JobsChartSegments.Technician;
            case "source": return JobsChartSegments.JobSource;
            case "type": return JobsChartSegments.JobType;
            case "metro": return JobsChartSegments.Metro;
            case "payments": return JobsChartSegments.Payments;
            case "closed": return JobsChartSegments.ClosedJobs;
            case "canceled": return JobsChartSegments.CanceledJobs;
        }
        return undefined;
    }

    const updateSegment = (s: JobsChartSegments) => {
        if (!body) return;
        navigate("/reports/jobs/" + segmentToSlug(s))
    }

    // Date basis and timezone belong to ONE report, not to all of them: picking "Created
    // Date" on Technician must not silently change Closed Jobs. They are stored per segment,
    // the same way the column visibility already is. An absent dateType means that report is
    // still on its own default (Agents by creation date, the rest by last status update).
    const basisStorageKey = (seg: JobsChartSegments) => "jobs_reports_basis_" + seg.toString()

    const loadSegmentBasis = (seg: JobsChartSegments): { dateType?: string, timezone?: string } => {
        try {
            var raw = window.localStorage.getItem(basisStorageKey(seg))
            return raw ? JSON.parse(raw) : {}
        }
        catch { return {} }
    }

    const saveSegmentBasis = (seg: JobsChartSegments, dateType?: string, timezone?: string) => {
        try {
            window.localStorage.setItem(basisStorageKey(seg), JSON.stringify({ dateType, timezone }))
        }
        catch { }
    }

    useEffect(() => {
        if (!body) return;
        const seg = slugToSegment(segment);

        // An unrecognised slug used to leave the previous report on screen with the wrong URL
        // above it -- a link or a bookmark then showed numbers for a report nobody asked for,
        // silently. Put the URL back on the report actually being shown.
        if (segment && seg === undefined) {
            navigate("/reports/jobs/" + segmentToSlug(body.segment), { replace: true });
            return;
        }

        if (seg !== undefined && seg !== body.segment) {
            clearFiltersSet(true)
            loadingSet(true)
            selectedJobsSet([])
            selectedJobSet(undefined)
            segmentIdsSet([])
            segmentsDataSet([])
            selectedSegmentItemsSet([])
            selectedSegmentItemSet(undefined);
            // Swap in the new report's own basis in the SAME update, so switching reports
            // costs one fetch and never carries the previous report's basis across. Both
            // fields are written explicitly -- spreading a partial would leave the old
            // report's values in place.
            var basis = loadSegmentBasis(seg)
            setJobsReportBody({
                ...body,
                segment: seg,
                segmentIds: [],
                dateType: basis.dateType,
                timezone: basis.timezone ?? "job"
            })
        }
    }, [segment, body?.segment])

    // Same reason as selectedTimeZone above: keep the saved timezone in the options even when
    // the server's list no longer offers that id, so the settings dropdown shows the real
    // value instead of falling back to its "Choose One" placeholder.
    const timezoneOptions = () => {
        if (!body?.timezone || body.timezone == "job") return timezonesSource;
        if (timezonesSource.some(t => t.id == body.timezone)) return timezonesSource;
        return [...timezonesSource, { id: body.timezone, name: body.timezone }];
    }

    // The toolbar shows the selected range as text now that the date select lives in the
    // settings drawer.
    const selectedDateName = () => {
        if (!body) return "";
        if (body.dateId == "99") return (body.startDate ?? "") + " - " + (body.toDate ?? "");
        var d = dates.filter(f => f.id == body.dateId)[0];
        return d?.name ?? "";
    }

    const segmentItemName = (s?: JobsChartSegments) => {
        if (!body) return "";
        if ((s ?? body.segment) == JobsChartSegments.Dispatchers) return "Dispatcher";
        //else if ((s ?? body.segment) == JobsChartSegments.Flags) return "Flag";
        else if ((s ?? body.segment) == JobsChartSegments.JobSource) return "Source";
        else if ((s ?? body.segment) == JobsChartSegments.ClosedJobs) return "";
        else if ((s ?? body.segment) == JobsChartSegments.CanceledJobs) return "";
        else if ((s ?? body.segment) == JobsChartSegments.JobType) return "Type";
        else if ((s ?? body.segment) == JobsChartSegments.Metro) return "Metro";
        else if ((s ?? body.segment) == JobsChartSegments.Payments) return "Technician";
        else if ((s ?? body.segment) == JobsChartSegments.Technician) return "Technician";
        return "";
    }



    const selectSegmentItem = (item: TagifyDataItem) => {

        if (!body) return;
        var arr = selectedSegmentItems ?? [];
        var ids = segmentIds ?? [];

        if (ids.indexOf(item.value) > -1) {
            arr.splice(ids.indexOf(item.value), 1);
            ids.splice(ids.indexOf(item.value), 1);
        }
        else {
            if (selectedSegmentItem) {
                arr.splice(ids.indexOf(selectedSegmentItem.value), 1);
                ids.splice(ids.indexOf(selectedSegmentItem.value), 1);
            }
            arr.push(item);
            ids.push(item.value);
        }

        selectedSegmentItemsSet(s => [...arr]);
        segmentIdsSet(s => [...ids]);
        setJobsReportBody({
            ...body,
            segmentIds: ids
        });
        window.closeModal("modal-segments-items");
    }

    const removeSegmentItem = (id: string) => {

        if (!body) return;
        var arr = selectedSegmentItems ?? [];
        var ids = segmentIds ?? [];

        if (ids.indexOf(id) > -1) {
            arr.splice(ids.indexOf(id), 1);
            ids.splice(ids.indexOf(id), 1);
        }

        if (selectedSegmentItem) {
            arr.splice(ids.indexOf(selectedSegmentItem.value), 1);
            ids.splice(ids.indexOf(selectedSegmentItem.value), 1);
        }

        selectedSegmentItemsSet(s => [...arr]);
        segmentIdsSet(s => [...ids]);
        setJobsReportBody({
            ...body,
            segmentIds: ids
        });

    }

    const segmentSelected = (id: string) => {
        for (var i = 0; i < selectedSegmentItems.length; i++) {
            if (selectedSegmentItems[i].value == id) return true;
        }
        return false;
    }

    const addSegmentItem = (item?: TagifyDataItem) => {
        selectedSegmentItemSet(item);
        fetchSegmentData("", item);
        addSegmentSet(true);
    }

    const SortIcon = (props: { sortKey: string }) => (
        <>
            {
                sortBody.sortKey == props.sortKey ?
                    <>
                        {
                            sortBody.sortLoading ?
                                <Icon icon="spinner-third" spin />
                                :
                                <Icon icon={sortBody.sortType == SortTypes.Ascending ? "sort-down" : "sort-up"} type="solid" />
                        }
                    </>
                    : <></>
            }
        </>
    )


    const [selectedJobs, selectedJobsSet] = useState<string[]>([]);

    const [selectedJob, selectedJobSet] = useState<Job>();
    const [reportIssueModal, reportIssueModalSet] = useState<boolean>(false);

    const [exportModal, exportModalSet] = useState<boolean>(false)

    const [dataColsVisibility, dataColsVisibilitySet] = useState<DataColumnsVisibility>()
    const [dataStatusCols, dataStatusColsSet] = useState<Array<string>>([])
    const [dataColsCount, dataColsCountSet] = useState<number>(0)
    useEffect(() => {
        if (!body?.segment) return;
        if (!data) return;
        if (data.length < 1) return;

        var str = window.localStorage.getItem("jobs_reports_dataColsVisibility_" + body.segment.toString())

        if (str) {
            var dcv: DataColumnsVisibility = JSON.parse(str)
            dataColsVisibilitySet(dcv)
            dataStatusColsSet(dcv.statuses ?? [])
        }
        else {
            var stIds: string[] = [];
            data[0].statusCounts.forEach(s => stIds.push(s.id!))

            var dv: DataColumnsVisibility = {
                avgtick: true,
                convrate: true,
                gross: true,
                jobs: true,
                profit: true
            }
            dataColsVisibilitySet(dv)
            dataStatusColsSet(stIds)
        }

    }, [body?.segment, data])

    useEffect(() => {
        if (dataColsVisibility) {
            var i = 1;
            if (dataColsVisibility.avgtick) i++
            if (dataColsVisibility.convrate) i++
            if (dataColsVisibility.gross) i++
            if (dataColsVisibility.jobs) i++
            if (dataColsVisibility.profit) i++

            const liveStatusIds = data?.[0]?.statusCounts?.map(s => s.id).filter(Boolean) as string[] | undefined
            const validStatusCols = (dataStatusCols ?? []).filter(id => !liveStatusIds || liveStatusIds.includes(id))

            if (validStatusCols.length !== (dataStatusCols ?? []).length) {
                dataStatusColsSet(validStatusCols)
                return
            }

            i += validStatusCols.length
            dataColsCountSet(i)

            var v = dataColsVisibility ?? {};
            v.statuses = validStatusCols;
            window.localStorage.setItem("jobs_reports_dataColsVisibility_" + body?.segment.toString(), JSON.stringify(v))
        }
    }, [dataColsVisibility, dataStatusCols, data])

    const [jobsColsVisibility, jobsColsVisibilitySet] = useState<JobsColumnsVisibility>()
    const [jobsColsCount, jobsColsCountSet] = useState<number>(0)
    useEffect(() => {
        if (!body?.segment) return;
        var jcv = window.localStorage.getItem("jobs_reports_jobsColsVisibility_" + body.segment.toString())

        if (jcv) jobsColsVisibilitySet(JSON.parse(jcv))
        else {
            if (body.segment == JobsChartSegments.CanceledJobs) {
                var v: JobsColumnsVisibility = {
                    name: true,
                    address: true,
                    type: true,
                    source: true,
                    tech: true,
                    createdDate: true,
                    lastUpdate: true,
                    cancelNote: true
                }
                jobsColsVisibilitySet(v)

            }
            else {
                var v: JobsColumnsVisibility = {
                    name: true,
                    address: true,
                    type: true,
                    source: true,
                    createdDate: true,
                    lastUpdate: true,
                    total: true,
                    cash: true,
                    ccmanual: true,
                    cccharge: true,
                    check: true,
                    coParts: true,
                    techParts: true,
                    coProfits: true,
                    techProfits: true,
                    sourceProfits: true,
                    techBalance: true
                }
                jobsColsVisibilitySet(v)
            }
        }
    }, [body?.segment])

    useEffect(() => {
        if (jobsColsVisibility) {
            var i = 0;
            Object.entries(jobsColsVisibility).forEach(([key, value]) => {
                if (value) i++;
            });
            jobsColsCountSet(i)
            window.localStorage.setItem("jobs_reports_jobsColsVisibility_" + body?.segment.toString(), JSON.stringify(jobsColsVisibility))
        }
    }, [jobsColsVisibility])


    const [techColsVisibility, techColsVisibilitySet] = useState<TechPayColumnsVisibility>()
    const [techColsCount, techColsCountSet] = useState<number>(0)
    useEffect(() => {
        if (!body?.segment) return;
        var tcv = window.localStorage.getItem("jobs_reports_techColsVisibility_" + body.segment.toString())
        if (tcv) techColsVisibilitySet(JSON.parse(tcv))
        else {
            var v: TechPayColumnsVisibility = {
                cash: true,
                check: true,
                closedJobs: true,
                metro: true,
                techBalance: true
            }
            techColsVisibilitySet(v)
        }
    }, [body?.segment])
    useEffect(() => {
        if (techColsVisibility && body) {
            var i = 1;
            Object.entries(techColsVisibility).forEach(([key, value]) => {
                if (value) i++;
            });
            techColsCountSet(i)
            window.localStorage.setItem("jobs_reports_techColsVisibility_" + body.segment.toString(), JSON.stringify(techColsVisibility))
        }
    }, [techColsVisibility])





    const getLastSentColor = (lastReportSent?: Date): string => {
        if (!lastReportSent) return "";

        const reportDate = new Date(lastReportSent);
        const today = new Date();
        const diffDays = Math.floor((today.getTime() - reportDate.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays <= 3) return "text-success";
        if (diffDays <= 6) return "text-warning";
        return "text-danger";
    };

    useEffect(() => {
        selectedJobsSet([])
    }, [body?.segmentIds])

    const getWidthStyle = (w: number) => {
        return { width: w, minWidth: w, maxWidth: w }
    }

    return (
        <>
            {
                body &&
                <>

                    <div className="p-5">

                        <div id="job-reports-stickybar">

                            <FlexRow>

                                <div className="p-3 d-inline-flex align-items-center">
                                    <Icon icon={segmentIcon()} type="solid" size={14} className="me-2" />
                                    <span className="text-dark fw-bolder d-block fs-7">
                                        {segmentTitle()}
                                    </span>
                                </div>

                                {
                                    selectedSegmentItems.map((item, key) =>
                                        <label key={key} className="btn btn-outline btn-outline-dashed btn-outline-default p-3 min-w-150px d-flex align-items-center active ms-3" onClick={e => {
                                            if ($(e.target).hasClass("remove-segment-item")) return;
                                            addSegmentItem(item);
                                        }}>
                                            {
                                                body.segment == JobsChartSegments.Dispatchers || body.segment == JobsChartSegments.Technician ?
                                                    <Avatar avatarUrl={item.avatar} width={16} fullName={item.title} />
                                                    : <></>
                                            }

                                            <span className="text-dark d-block fs-7 ms-2">
                                                {item.title}
                                            </span>
                                            <Icon icon="times" className="ms-auto remove-segment-item"
                                                onClick={e => {
                                                    removeSegmentItem(item.value);
                                                }} />
                                        </label>
                                    )
                                }

                                {
                                    selectedSegmentItems.length < 3 && body.segment != JobsChartSegments.CanceledJobs && body.segment != JobsChartSegments.ClosedJobs &&
                                    <label className="btn btn-outline btn-outline-dashed btn-outline-default p-3 min-w-150px d-flex align-items-center ms-3" onClick={e => addSegmentItem()}>
                                        <Icon icon="plus" type="solid" size={14} className="me-2" />
                                        <span className="text-gray-500 d-block fs-7">
                                            Add {segmentItemName()}
                                        </span>
                                    </label>
                                }

                                <FlexRow className="ms-auto">

                                    {/* What the numbers on screen actually mean: which date the range is
                                        matched against and in whose clock. Two small muted lines so it costs
                                        no extra row, and clicking them opens the settings drawer. */}
                                    <div className="me-3 text-end cursor-pointer"
                                        title="Report basis - click to change"
                                        onClick={() => columnsDrawerSet(true)}>
                                        <div className="fs-7 fw-bold text-gray-800 lh-1">
                                            {selectedDateName()}
                                        </div>
                                        <div className="fs-8 text-gray-500 lh-1 mt-1">
                                            {dateTypeName(effectiveDateType(body))} &middot; {selectedTimeZone?.name ?? "Job Timezone"}
                                        </div>
                                    </div>

                                    {/* Always available: the drawer holds the date range now, so hiding
                                        it on an empty report would leave no way to change the range. */}
                                    <button type="button"
                                        className="btn btn-sm btn-icon btn-light-primary me-2"
                                        title="Report Settings"
                                        onClick={e => {
                                            e.preventDefault()
                                            columnsDrawerSet(true)
                                        }}>
                                        <Icon icon="gear" size={14} />
                                    </button>

                                    {
                                        ((data && data.length > 0) || (payments && payments.length > 0) || (jobs && jobs.length > 0)) &&
                                        <button type="button"
                                            className="btn btn-sm btn-icon btn-light-primary me-2"
                                            title="Send Report"
                                            onClick={e => {
                                                e.preventDefault()
                                                exportModalSet(true)
                                            }}>
                                            <Icon icon="arrow-right-from-file" size={14} />
                                        </button>
                                    }

                                    <button type="button"
                                        className="btn btn-sm btn-icon btn-light-primary"
                                        title="Refresh"
                                        disabled={loading}
                                        onClick={e => {
                                            e.preventDefault()
                                            fetch()
                                        }}>
                                        <Icon icon={`${loading ? "spinner-third" : "refresh"}`} spin={loading} size={14} />
                                    </button>

                                </FlexRow>

                            </FlexRow>

                            <div className="mt-3">
                                <Filtering id="filtering-reports" clear={clearFilters}
                                    filteringProps={body.segment == JobsChartSegments.Dispatchers ? FilteringPropsAgents
                                        : body.segment == JobsChartSegments.Technician ? FilteringPropsTechs
                                            : body.segment == JobsChartSegments.JobSource ? FilteringPropsSources
                                                : body.segment == JobsChartSegments.JobType ? FilteringPropsTypes
                                                    : body.segment == JobsChartSegments.Metro ? FilteringPropsMetros
                                                        //: body.segment == JobsChartSegments.Flags ? FilteringPropsFlags
                                                        : body.segment == JobsChartSegments.Payments && body.segmentIds.length > 0 ? FilteringPropsPaymentsDetails
                                                            : body.segment == JobsChartSegments.Payments ? FilteringPropsPayments
                                                                : body.segment == JobsChartSegments.ClosedJobs ? FilteringPropsClosedJobs
                                                                    : body.segment == JobsChartSegments.CanceledJobs ? FilteringPropsClosedJobs
                                                                        : []}
                                    apiRoute={`/reports/jobs/data/${body.segment}/`}
                                    filteringValues={filteringValues}
                                    loaded={loaded}
                                    onItemsChanged={(items, q) => {
                                        filterQuerySet(q)
                                    }} />
                            </div>
                        </div>

                        <div>

                            <Div className="mt-20" visible={loading}>
                                <Indicator />
                            </Div>
                            <Div visible={!loading}>

                                {
                                    body.segment == JobsChartSegments.CanceledJobs ? <CancelReasonsCard items={model?.cancelReasons}
                                        selected={cancelReasonKeys} shownJobs={jobs?.length}
                                        onSelectedChange={cancelReasonKeysSet} />
                                        : body.segment == JobsChartSegments.ClosedJobs ? <></>
                                            : body.segment == JobsChartSegments.Payments ? <></>
                                                : body.segmentIds.length > 0 ? <></>
                                                    :
                                                    <div className="card mt-2">
                                                        <div className="card-body">
                                                            <FlexRow>

                                                                <FlexRow>

                                                                    <div style={{
                                                                        position: "relative",
                                                                        width: 160,
                                                                        height: 160
                                                                    }}>
                                                                        <div id="jobchart-circle" data-kt-size="160" data-kt-line="20"></div>

                                                                        <div style={{
                                                                            position: "absolute",
                                                                            top: 60,
                                                                            right: 0,
                                                                            width: 160,
                                                                            height: 160,
                                                                            textAlign: "center"
                                                                        }}>

                                                                            <div className="fs-3 fw-bolder text-center">
                                                                                Total Jobs
                                                                            </div>
                                                                            <div className="fs-2 fw-bolder text-center">
                                                                                {model?.pieChart?.total}
                                                                            </div>

                                                                        </div>
                                                                    </div>

                                                                    <div className="ms-10">
                                                                        <div className="row">
                                                                            {
                                                                                model?.pieChart?.statusCounts.map((st, k) =>
                                                                                    <div key={k} className="col col-md-6">
                                                                                        <FlexRow key={k} alignItems="start" className="mb-3">
                                                                                            <div>
                                                                                                <Icon icon="circle" type="solid" color={st.color} size={12} />
                                                                                            </div>
                                                                                            <div className="ms-2">
                                                                                                <div className="fw-bold" style={{ minWidth: 120 }}>
                                                                                                    {st.name}
                                                                                                </div>
                                                                                                <FlexRow>
                                                                                                    <div className="fw-bold">
                                                                                                        {st.count}
                                                                                                    </div>
                                                                                                    <div className="ms-2 text-muted fs-8">
                                                                                                        ({st.percent}%)
                                                                                                    </div>
                                                                                                </FlexRow>
                                                                                            </div>
                                                                                        </FlexRow>
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    </div>

                                                                </FlexRow>

                                                                <FlexRow className="ms-auto">

                                                                    {/* <div className="card overflow-hidden h-md-50 me-2">
                                                                <div className="card-body bg-primary d-flex justify-content-between flex-column px-0 pb-0">
                                                                    <div className="mb-4 px-4" style={{ minWidth: 200 }}>
                                                                        <div className="d-flex align-items-center mb-2">
                                                                            <span className="fs-4 fw-bold text-gray-200 align-self-start me-1>">%</span>
                                                                            <span className="fs-2hx fw-bolder text-white me-2 lh-1">{model?.callsToJobRate}</span>
                                                                        </div>
                                                                        <span className="fs-6 fw-bold text-gray-300">Calls to Job CR</span>
                                                                    </div>
                                                                    <div id="kt_card_widget_1_chart" className="min-h-auto" style={{ height: 100 }}></div>
                                                                </div>
                                                            </div> */}

                                                                    <div className="card overflow-hidden h-md-50 me-2">
                                                                        <div className="card-body bg-info d-flex justify-content-between flex-column px-0 pb-0">
                                                                            <div className="mb-4 px-4" style={{ minWidth: 200 }}>
                                                                                <div className="d-flex align-items-center mb-2">
                                                                                    <span className="fs-4 fw-bold text-gray-200 align-self-start me-1>">%</span>
                                                                                    <span className="fs-2hx fw-bolder text-white me-2 lh-1">{model?.jobsToClosedRate}</span>
                                                                                </div>
                                                                                <span className="fs-6 fw-bold text-gray-300">Jobs to Closed CR</span>
                                                                            </div>
                                                                            <div id="kt_card_widget_2_chart" className="min-h-auto" style={{ height: 100 }}></div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="card overflow-hidden h-md-50">
                                                                        <div className="card-body bg-danger d-flex justify-content-between flex-column px-0 pb-0">
                                                                            <div className="mb-4 px-4" style={{ minWidth: 200 }}>
                                                                                <div className="d-flex align-items-center mb-2">
                                                                                    <span className="fs-4 fw-bold text-gray-200 align-self-start me-1>">$</span>
                                                                                    <span className="fs-2hx fw-bolder text-white me-2 lh-1">
                                                                                        {moneyFormatter.format(avTickets ?? 0).replace("$", "")}
                                                                                    </span>
                                                                                </div>
                                                                                <span className="fs-6 fw-bold text-gray-300">Average Tickets</span>
                                                                            </div>
                                                                            <div id="kt_card_widget_3_chart" className="min-h-auto" style={{ height: 100 }}></div>
                                                                        </div>
                                                                    </div>

                                                                </FlexRow>

                                                            </FlexRow>
                                                        </div>
                                                    </div>
                                }

                                {
                                    data && data.length > 0 &&
                                    <div className="card mt-2">
                                        <div className="card-body">
                                            <table className="table table-hover table-sm align-middle table-row-dashed">
                                                <thead>
                                                    <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                                                        <th>
                                                            <FlexRow onClick={e => {
                                                                sortBodySet({
                                                                    sortKey: "name",
                                                                    sortLoading: true,
                                                                    sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                })
                                                            }}>
                                                                <span className="me-2">Name</span>
                                                                <SortIcon sortKey="name" />
                                                            </FlexRow>
                                                        </th>

                                                        {
                                                            dataColsVisibility?.jobs &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "jobs",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Jobs</span>
                                                                    <SortIcon sortKey="jobs" />
                                                                </FlexRow>
                                                            </th>
                                                        }

                                                        {
                                                            data[0].statusCounts.filter(f => dataStatusCols.includes(f.id!)).map((st, k) =>
                                                                <th key={k}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "status-" + st.id,
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">{st.name}</span>
                                                                        <SortIcon sortKey={"status-" + st.id} />
                                                                    </FlexRow>
                                                                </th>
                                                            )
                                                        }

                                                        {
                                                            dataColsVisibility?.convrate &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "conv",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">CONV Rate</span>
                                                                    <SortIcon sortKey="conv" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            dataColsVisibility?.avgtick &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "avgtickets",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Avg tickets</span>
                                                                    <SortIcon sortKey="avgtickets" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            dataColsVisibility?.gross &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "totalgross",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Total Gross</span>
                                                                    <SortIcon sortKey="totalgross" />
                                                                </FlexRow>

                                                            </th>
                                                        }
                                                        {
                                                            dataColsVisibility?.sourceprofit && body.segment == JobsChartSegments.JobSource &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "sourceprofit",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Source Profit</span>
                                                                    <SortIcon sortKey="sourceprofit" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            dataColsVisibility?.profit &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "totalprofit",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Total Profit</span>
                                                                    <SortIcon sortKey="totalprofit" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            body.segment == JobsChartSegments.JobSource &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "balance",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Balance</span>
                                                                    <SortIcon sortKey="balance" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        data.map((row, k) =>
                                                            <tr key={k} className={"fs-8" + (row.id == "total" ? " fw-bolder reports-total-row" : "")}>
                                                                <td className="ps-1">
                                                                    {
                                                                        row.id == "total" ?
                                                                            <span>{row.name}</span>
                                                                            :
                                                                            <a href="#"
                                                                                onClick={e => {
                                                                                    e.preventDefault();
                                                                                    selectSegmentItem({
                                                                                        title: row.name ?? "",
                                                                                        value: row.id ?? "",
                                                                                        avatar: row.avatar
                                                                                    });
                                                                                }}>
                                                                                {row.name}
                                                                            </a>
                                                                    }
                                                                </td>
                                                                {
                                                                    dataColsVisibility?.jobs &&
                                                                    <td>
                                                                        {
                                                                            row.totalJobs > 0 ?
                                                                                <a href="#" onClick={e => {
                                                                                    e.preventDefault()
                                                                                    openReportJobsList({
                                                                                        name: row.name!,
                                                                                        dateId: body.dateId,
                                                                                        timezone: body.timezone,
                                                                                        segment: body.segment,
                                                                                        startDate: body.startDate,
                                                                                        toDate: body.toDate,
                                                                                        dateType: body.dateType,
                                                                                        filterQuery: filterQuery,
                                                                                        segmentId: row.id
                                                                                    })
                                                                                }}>
                                                                                    {row.totalJobs}
                                                                                </a>
                                                                                :
                                                                                <>{row.totalJobs}</>
                                                                        }
                                                                    </td>
                                                                }

                                                                {
                                                                    row.statusCounts.filter(f => dataStatusCols.includes(f.id!)).map((st, sk) =>
                                                                        <td key={sk}>
                                                                            {
                                                                                st.isCanceledStatus || st.isClosedStatus ?
                                                                                    <div style={{ width: 30, height: 30 }} title={`${st.percent}%`}>
                                                                                        {
                                                                                            st.jobsCount && st.jobsCount > 0 ?
                                                                                                <a href="#" onClick={e => {
                                                                                                    e.preventDefault()
                                                                                                    openReportJobsList({
                                                                                                        name: row.name!,
                                                                                                        segment: body.segment,
                                                                                                        segmentId: row.id,
                                                                                                        dateId: body.dateId,
                                                                                                        startDate: body.startDate,
                                                                                                        timezone: body.timezone,
                                                                                                        toDate: body.toDate,
                                                                                                        dateType: body.dateType,
                                                                                                        filterQuery: filterQuery,
                                                                                                        status: st
                                                                                                    })
                                                                                                }}>
                                                                                                    <CircularProgressbar
                                                                                                        value={st.percent ?? 0}
                                                                                                        text={st.jobsCount?.toString()}
                                                                                                        styles={buildStyles({
                                                                                                            textColor: "#333",
                                                                                                            textSize: 34,
                                                                                                            pathColor: st.color
                                                                                                        })}
                                                                                                    />
                                                                                                </a>
                                                                                                :
                                                                                                <CircularProgressbar
                                                                                                    value={st.percent ?? 0}
                                                                                                    text={st.jobsCount?.toString()}
                                                                                                    styles={buildStyles({
                                                                                                        textColor: "#333",
                                                                                                        textSize: 34,
                                                                                                        pathColor: st.color
                                                                                                    })}
                                                                                                />
                                                                                        }

                                                                                    </div>
                                                                                    :
                                                                                    <>
                                                                                        {
                                                                                            st.jobsCount && st.jobsCount > 0 ?
                                                                                                <a href="#" onClick={e => {
                                                                                                    e.preventDefault()
                                                                                                    openReportJobsList({
                                                                                                        name: row.name!,
                                                                                                        segment: body.segment,
                                                                                                        segmentId: row.id,
                                                                                                        dateId: body.dateId,
                                                                                                        startDate: body.startDate,
                                                                                                        timezone: body.timezone,
                                                                                                        toDate: body.toDate,
                                                                                                        dateType: body.dateType,
                                                                                                        filterQuery: filterQuery,
                                                                                                        status: st
                                                                                                    })
                                                                                                }}>
                                                                                                    {st.jobsCount}
                                                                                                </a>
                                                                                                :
                                                                                                <>{st.jobsCount}</>
                                                                                        }
                                                                                    </>
                                                                            }
                                                                        </td>
                                                                    )
                                                                }

                                                                {
                                                                    dataColsVisibility?.convrate &&
                                                                    <td>
                                                                        <div style={{ width: 30, height: 30 }}>
                                                                            <CircularProgressbar
                                                                                value={row.conversionRate}
                                                                                text={`${row.conversionRate}%`}
                                                                                styles={{
                                                                                    text: {
                                                                                        fontSize: 30,
                                                                                        fill: "#333"
                                                                                    }
                                                                                }} />
                                                                        </div>
                                                                    </td>
                                                                }

                                                                {
                                                                    dataColsVisibility?.avgtick && <td>{moneyFormatter.format(row.averageTickets ?? 0)}</td>
                                                                }
                                                                {
                                                                    dataColsVisibility?.gross && <td>{moneyFormatter.format(row.totalGross ?? 0)}</td>
                                                                }
                                                                {
                                                                    dataColsVisibility?.sourceprofit && body.segment == JobsChartSegments.JobSource &&
                                                                    <td>{moneyFormatter.format(row.sourceProfit ?? 0)}</td>
                                                                }
                                                                {
                                                                    dataColsVisibility?.profit && <td>{moneyFormatter.format(row.totalProfit ?? 0)}</td>
                                                                }

                                                                {
                                                                    body.segment == JobsChartSegments.JobSource &&
                                                                    <td className={(row.sourceBalance ?? 0) > 0 ? "text-danger" : ""}>
                                                                        {(row.sourceBalance ?? 0) > 0 ? "-" : ""}
                                                                        {moneyFormatter.format(row.sourceBalance ?? 0)}
                                                                    </td>
                                                                }


                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                }

                                {
                                    payments && payments.length > 0 &&
                                    <div className="card">
                                        <div className="card-body">
                                            <table className="table table-hover table-sm align-middle table-row-dashed">
                                                <thead>
                                                    <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                                                        <th>
                                                            <FlexRow onClick={e => {
                                                                sortBodySet({
                                                                    sortKey: "tech",
                                                                    sortLoading: true,
                                                                    sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                })
                                                            }}>
                                                                <span className="me-2">Technician</span>
                                                                <SortIcon sortKey="tech" />
                                                            </FlexRow>
                                                        </th>
                                                        {
                                                            techColsVisibility?.phone &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "phone",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Phone</span>
                                                                    <SortIcon sortKey="phone" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.metro && <th>Metro</th>
                                                        }
                                                        {
                                                            techColsVisibility?.type && <th>Types</th>
                                                        }
                                                        {
                                                            techColsVisibility?.source && <th>Sources</th>
                                                        }

                                                        {
                                                            techColsVisibility?.closedJobs &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "closed",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Closed Jobs</span>
                                                                    <SortIcon sortKey="closed" />
                                                                </FlexRow>
                                                            </th>
                                                        }

                                                        {
                                                            techColsVisibility?.total &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "total",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Total Sale</span>
                                                                    <SortIcon sortKey="total" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.cash &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "cash",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Cash</span>
                                                                    <SortIcon sortKey="cash" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.cccharge &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "cccharge",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">CC Charge</span>
                                                                    <SortIcon sortKey="cccharge" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.ccmanual &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "ccmanual",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">CC Manual</span>
                                                                    <SortIcon sortKey="ccmanual" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.check &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "check",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Check</span>
                                                                    <SortIcon sortKey="check" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.epay &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "epay",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">E-Pay</span>
                                                                    <SortIcon sortKey="epay" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.rinvoxPay &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "rinvoxpay",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Rinvox Pay</span>
                                                                    <SortIcon sortKey="rinvoxpay" />
                                                                </FlexRow>
                                                            </th>
                                                        }
                                                        {
                                                            techColsVisibility?.techBalance &&
                                                            <th>
                                                                <FlexRow onClick={e => {
                                                                    sortBodySet({
                                                                        sortKey: "balance",
                                                                        sortLoading: true,
                                                                        sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                    })
                                                                }}>
                                                                    <span className="me-2">Balance</span>
                                                                    <SortIcon sortKey="balance" />
                                                                </FlexRow>
                                                            </th>
                                                        }

                                                        <th>
                                                            <FlexRow onClick={e => {
                                                                sortBodySet({
                                                                    sortKey: "lastSent",
                                                                    sortLoading: true,
                                                                    sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                })
                                                            }}>
                                                                <span className="me-2">Last Sent Date</span>
                                                                <SortIcon sortKey="lastSent" />
                                                            </FlexRow>
                                                        </th>

                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        payments.map(row =>
                                                            <tr key={`payment-row-${row.tech?.id}`} className={"fs-8" + (row.isTotalRow ? " fw-bolder reports-total-row" : "")}>
                                                                <td className="ps-1">
                                                                    {
                                                                        row.isTotalRow ?
                                                                            <span>Total</span>
                                                                            :
                                                                            <a href="#"
                                                                                onClick={e => {
                                                                                    e.preventDefault();
                                                                                    selectSegmentItem({
                                                                                        title: row.tech?.fullName ?? "",
                                                                                        value: row.tech?.id ?? "",
                                                                                        avatar: row.tech?.avatarUrl ?? ""
                                                                                    });
                                                                                }}>
                                                                                {row.tech?.fullName}
                                                                            </a>
                                                                    }
                                                                </td>

                                                                {
                                                                    techColsVisibility?.phone && <td>{row.tech?.phoneNumber}</td>
                                                                }
                                                                {
                                                                    techColsVisibility?.metro &&
                                                                    <td>
                                                                        <div className="limited-text mw-200px" title={row.metros}>
                                                                            {row.metros}
                                                                        </div>
                                                                    </td>
                                                                }
                                                                {
                                                                    techColsVisibility?.type && <td>{row.types}</td>
                                                                }
                                                                {
                                                                    techColsVisibility?.source && <td>{row.sources}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.closedJobs && <td>{row.closedJobs}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.total && <td>{moneyFormatter.format(row.total ?? 0)}</td>
                                                                }
                                                                {
                                                                    techColsVisibility?.cash && <td>{moneyFormatter.format(row.cash ?? 0)}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.cccharge && <td>{moneyFormatter.format(row.cccharge ?? 0)}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.ccmanual && <td>{moneyFormatter.format(row.ccmanual ?? 0)}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.check && <td>{moneyFormatter.format(row.check ?? 0)}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.epay && <td>{moneyFormatter.format(row.epay ?? 0)}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.rinvoxPay && <td>{moneyFormatter.format(row.rinvoxPay ?? 0)}</td>
                                                                }

                                                                {
                                                                    techColsVisibility?.techBalance &&
                                                                    <td className={(row.balance ?? 0) < 0 ? "text-danger" : (row.balance ?? 0) > 0 ? "text-success" : ""}>
                                                                        {(row.balance ?? 0) < 0 ? "" : (row.balance ?? 0) > 0 ? "+" : ""}
                                                                        {moneyFormatter.format(row.balance ?? 0)}
                                                                    </td>
                                                                }
                                                                <td>
                                                                    {
                                                                        row.lastReportSent ?
                                                                            <div className={`${getLastSentColor(row.lastReportSent)}`}>
                                                                                {toPrettyDate(row.lastReportSent)}
                                                                            </div>
                                                                            :
                                                                            <span>-</span>
                                                                    }
                                                                </td>
                                                                <td>
                                                                    {
                                                                        !row.isTotalRow &&
                                                                        <a href="#" className="badge badge-light-primary" title="Send Report"
                                                                            onClick={e => {
                                                                                e.preventDefault()
                                                                                exportModalSet(true)
                                                                                defaultSegmentIdSet(row.tech?.id)
                                                                                exportTechIdSet(row.tech?.id)
                                                                            }}>
                                                                            <Icon icon="share" className="me-2" />
                                                                        </a>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                }

                                {
                                    jobs && jobs.length > 0 &&
                                    <div className="card mt-2">
                                        <div className="card-body p-0">
                                            <div className="reports-table-wrapper"
                                                style={{
                                                    height: boxHeight
                                                }}>
                                                <table className="table table-hover table-sm align-middle table-row-dashed reports-sticky-table">
                                                    <thead id="jobs-table-thead">
                                                        <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                                                            {
                                                                (body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) &&
                                                                <th className="ps-5" style={getWidthStyle(40)}>
                                                                    <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                                        <input className="form-check-input" type="checkbox" checked={selectedJobs.length == jobs.length}
                                                                            onChange={e => {
                                                                                if (e.target.checked) {
                                                                                    var arr = selectedJobs;
                                                                                    jobs.forEach(j => {
                                                                                        if (arr.indexOf(j.id!) < 0) {
                                                                                            arr.push(j.id!);
                                                                                        }
                                                                                    });
                                                                                    selectedJobsSet(j => [...arr]);
                                                                                }
                                                                                else {
                                                                                    selectedJobsSet(j => [...[]]);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </th>
                                                            }

                                                            {/* The row number header is merged with the column right after it, so the
                                                                number column itself stays as narrow as the numbers in it. That neighbour
                                                                is the settle column when there is one, otherwise Job ID - neither needs a
                                                                title of its own, so the merged cell carries "# N Jobs" for both. */}
                                                            <th colSpan={2}
                                                                className={(body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) ? "" : "ps-5"}
                                                                style={{
                                                                    ...getWidthStyle((body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) ? 40 + 165 : 40 + 80),
                                                                    textTransform: "capitalize"
                                                                }}>
                                                                <FlexRow>
                                                                    <div># {jobs.length} Jobs</div>
                                                                    {
                                                                        selectedJobs.length > 0 &&
                                                                        (body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) &&
                                                                        <div className="ms-4">
                                                                            <JobSettleMenu key={`job-settle-menu-multiple`}
                                                                                jobIds={selectedJobs}
                                                                                onAllSettled={() => {
                                                                                    if (!jobs) return;
                                                                                    jobsSet(prev => prev!.map(job => selectedJobs.includes(job.id!) ? { ...job, techSetteled: true, sourceSetteled: true } : job))
                                                                                    selectedJobsSet([])
                                                                                }}
                                                                                onTechSettled={() => {
                                                                                    if (!jobs) return;
                                                                                    jobsSet(prev => prev!.map(job => selectedJobs.includes(job.id!) ? { ...job, techSetteled: true, sourceSetteled: false } : job))
                                                                                    selectedJobsSet([])
                                                                                }}
                                                                                onSourceSettled={() => {
                                                                                    if (!jobs) return;
                                                                                    jobsSet(prev => prev!.map(job => selectedJobs.includes(job.id!) ? { ...job, techSetteled: false, sourceSetteled: true } : job))
                                                                                    selectedJobsSet([])
                                                                                }}
                                                                                onUnsettled={() => {
                                                                                    if (!jobs) return;
                                                                                    jobsSet(prev => prev!.map(job => selectedJobs.includes(job.id!) ? { ...job, techSetteled: false, sourceSetteled: false } : job))
                                                                                    selectedJobsSet([])
                                                                                }} />
                                                                        </div>
                                                                    }
                                                                </FlexRow>
                                                            </th>

                                                            {
                                                                (body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) &&
                                                                <th style={getWidthStyle(80)}>Job ID</th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.status &&
                                                                <th style={getWidthStyle(80)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "status",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Status</span>
                                                                        <SortIcon sortKey="status" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.name &&
                                                                <th style={getWidthStyle(65)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "name",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Name</span>
                                                                        <SortIcon sortKey="name" />
                                                                    </FlexRow>
                                                                </th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.phone &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "phone",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Phone</span>
                                                                        <SortIcon sortKey="phone" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.type &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "type",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Type</span>
                                                                        <SortIcon sortKey="type" />
                                                                    </FlexRow>
                                                                </th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.source &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "source",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Source</span>
                                                                        <SortIcon sortKey="source" />
                                                                    </FlexRow>
                                                                </th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.address &&
                                                                <th style={getWidthStyle(150)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "address",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Address</span>
                                                                        <SortIcon sortKey="address" />
                                                                    </FlexRow>
                                                                </th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.metro &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "metro",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Metro</span>
                                                                        <SortIcon sortKey="metro" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.tech &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "tech",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Tech</span>
                                                                        <SortIcon sortKey="tech" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.createdDate &&
                                                                <th style={getWidthStyle(120)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "createdAt",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Created</span>
                                                                        <SortIcon sortKey="createdAt" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.lastUpdate &&
                                                                <th style={getWidthStyle(120)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "lastUpdate",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">
                                                                            {
                                                                                body.segment == JobsChartSegments.CanceledJobs ? "Canceled" : body.segment == JobsChartSegments.ClosedJobs ? "Closed" : "Closed"
                                                                            }
                                                                        </span>
                                                                        <SortIcon sortKey="lastUpdate" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.apptDate &&
                                                                <th style={getWidthStyle(120)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "apptDate",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-1">Appt</span>
                                                                        <SortIcon sortKey="apptDate" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.cancelNote &&
                                                                <th style={getWidthStyle(200)}>
                                                                    Note
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.invNo &&
                                                                <th title="Invoice Number" style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "invNumber",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Inv NO</span>
                                                                        <SortIcon sortKey="invNumber" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.total &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "total",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Total</span>
                                                                        <SortIcon sortKey="total" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.cash &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "cash",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Cash</span>
                                                                        <SortIcon sortKey="cash" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.ccmanual &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "ccmanual",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">CC Manual</span>
                                                                        <SortIcon sortKey="ccmanual" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.cccharge &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "credit",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">CC Charge</span>
                                                                        <SortIcon sortKey="credit" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.cccharge &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "ccrate",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">CC Rate</span>
                                                                        <SortIcon sortKey="ccrate" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.check &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "check",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Check</span>
                                                                        <SortIcon sortKey="check" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.check &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "checkrate",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Check Rate</span>
                                                                        <SortIcon sortKey="checkrate" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.epay &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "epay",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">E-PAY</span>
                                                                        <SortIcon sortKey="epay" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.rinvoxPay &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "rinvoxpay",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">RINVOX PAY</span>
                                                                        <SortIcon sortKey="rinvoxpay" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.rinvoxPay &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "rinvoxrate",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">RINVOX RATE</span>
                                                                        <SortIcon sortKey="rinvoxrate" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.techParts &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "techparts",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Tech Parts</span>
                                                                        <SortIcon sortKey="techparts" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.coParts &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "coparts",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Co Parts</span>
                                                                        <SortIcon sortKey="coparts" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.tax &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "tax",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Tax</span>
                                                                        <SortIcon sortKey="tax" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.techProfits &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "techprofit",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Tech Profits</span>
                                                                        <SortIcon sortKey="techprofit" />
                                                                    </FlexRow>
                                                                </th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.sourceProfits &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "sourceprofit",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Source Profits</span>
                                                                        <SortIcon sortKey="sourceprofit" />
                                                                    </FlexRow>
                                                                </th>
                                                            }
                                                            {
                                                                jobsColsVisibility?.coProfits &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "coprofit",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Co Profits</span>
                                                                        <SortIcon sortKey="coprofit" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.techFee &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "techFee",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Tech Fee</span>
                                                                        <SortIcon sortKey="techFee" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                            {
                                                                jobsColsVisibility?.techBalance &&
                                                                <th style={getWidthStyle(100)}>
                                                                    <FlexRow onClick={e => {
                                                                        sortBodySet({
                                                                            sortKey: "techbalance",
                                                                            sortLoading: true,
                                                                            sortType: sortBody.sortType == SortTypes.Ascending ? SortTypes.Descending : SortTypes.Ascending
                                                                        })
                                                                    }}>
                                                                        <span className="me-2">Tech Balance</span>
                                                                        <SortIcon sortKey="techbalance" />
                                                                    </FlexRow>
                                                                </th>
                                                            }

                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            jobs.map((job, jobIndex) =>
                                                                <tr key={`job-row-${job.id}`} className={`fs-8 ${(selectedJobs.indexOf(job.id!) > -1 ? "job-row-selected" : "")}`}>
                                                                    {
                                                                        (body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) &&
                                                                        <>
                                                                            <td className="ps-5" style={getWidthStyle(40)}>
                                                                                <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                                                    <input className="form-check-input" type="checkbox" checked={selectedJobs.indexOf(job.id!) > -1}
                                                                                        onChange={e => {
                                                                                            if (e.target.checked) {
                                                                                                var arr = selectedJobs;
                                                                                                var exists = false;
                                                                                                for (var i = 0; i < arr.length; i++) {
                                                                                                    if (arr[i] == job.id) {
                                                                                                        exists = true;
                                                                                                        break;
                                                                                                    }
                                                                                                }
                                                                                                if (!exists) {
                                                                                                    arr.push(job.id!);
                                                                                                    selectedJobsSet(j => [...arr]);
                                                                                                }
                                                                                            }
                                                                                            else {
                                                                                                var arr = selectedJobs;
                                                                                                for (var i = 0; i < arr.length; i++) {
                                                                                                    if (arr[i] == job.id) {
                                                                                                        arr.splice(i, 1);
                                                                                                        break;
                                                                                                    }
                                                                                                }

                                                                                                selectedJobsSet(j => [...arr]);
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </td>
                                                                        </>
                                                                    }

                                                                    {/* Row number: always visible, not part of the column visibility options.
                                                                        The table uses auto layout, so spare width is spread over the columns and a
                                                                        left-aligned number drifts away from the next cell - right-aligning keeps it
                                                                        tight against the issue icon. When there is no checkbox column it is the first
                                                                        cell instead, and gets the edge padding and the wider "N Jobs" header. */}
                                                                    <td className={`text-muted ${(body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) ? "" : "ps-5"}`}
                                                                        style={getWidthStyle(40)}>
                                                                        {jobIndex + 1}
                                                                    </td>

                                                                    {
                                                                        (body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) &&
                                                                        <>
                                                                            <td style={getWidthStyle(165)}>
                                                                                <FlexRow>
                                                                                    <a href="#" title={job.hasIssue ? "Resolve Issue" : "Report Issue"} className="me-3"
                                                                                        onClick={e => {
                                                                                            e.preventDefault();
                                                                                            selectedJobSet(job);
                                                                                            reportIssueModalSet(true);
                                                                                        }}>
                                                                                        <Icon icon="circle-exclamation" size={18} className={job.hasIssue ? "text-danger" : "text-muted"} type={job.hasIssue ? "solid" : "regular"} />
                                                                                    </a>
                                                                                    <JobSettleMenu key={`job-settle-menu-${job.id}`} job={job}
                                                                                        onAllSettled={() => {
                                                                                            if (!jobs) return;
                                                                                            jobsSet(prev => prev!.map(j => j.id == job.id ? { ...j, techSetteled: true, sourceSetteled: true } : j))
                                                                                            selectedJobsSet([])
                                                                                        }}
                                                                                        onTechSettled={() => {
                                                                                            if (!jobs) return;
                                                                                            jobsSet(prev => prev!.map(j => j.id == job.id ? { ...j, techSetteled: true, sourceSetteled: false } : j))
                                                                                            selectedJobsSet([])
                                                                                        }}
                                                                                        onSourceSettled={() => {
                                                                                            if (!jobs) return;
                                                                                            jobsSet(prev => prev!.map(j => j.id == job.id ? { ...j, techSetteled: false, sourceSetteled: true } : j))
                                                                                            selectedJobsSet([])
                                                                                        }}
                                                                                        onUnsettled={() => {
                                                                                            if (!jobs) return;
                                                                                            jobsSet(prev => prev!.map(j => j.id == job.id ? { ...j, techSetteled: false, sourceSetteled: false } : j))
                                                                                            selectedJobsSet([])
                                                                                        }} />

                                                                                </FlexRow>
                                                                            </td>
                                                                        </>
                                                                    }

                                                                    <td style={getWidthStyle(80)}>
                                                                        <div>
                                                                            <a href="#" onClick={e => {
                                                                                e.preventDefault();
                                                                                if (job.id) openJobQuickView(job.id)
                                                                            }}>
                                                                                #{job.jobId}
                                                                            </a>
                                                                        </div>
                                                                    </td>

                                                                    {
                                                                        jobsColsVisibility?.status &&
                                                                        <td style={getWidthStyle(80)}>
                                                                            <div title={job.jobStatus?.name} className="limited-text" style={getWidthStyle(75)}>
                                                                                <Icon icon="circle" type="solid" color={job.jobStatus?.color} className="me-1" />
                                                                                {job.jobStatus?.name}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.name &&
                                                                        <td style={getWidthStyle(65)}>
                                                                            <div title={job.fullName} style={getWidthStyle(60)} className="limited-text">
                                                                                {job.fullName}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.phone &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <div title={job.phone?.phoneFriendly} style={getWidthStyle(90)} className="limited-text">
                                                                                {job.phoneRaw}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.type &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <div title={job.jobType} style={getWidthStyle(90)} className="limited-text">
                                                                                {job.jobType}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.source &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <div title={job.jobSource} style={getWidthStyle(90)} className="limited-text">
                                                                                {job.jobSource}
                                                                            </div>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.address &&
                                                                        <td style={getWidthStyle(150)}>
                                                                            <div title={job.address?.title} style={getWidthStyle(140)} className="limited-text">
                                                                                {job.address?.title}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.metro &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <div style={getWidthStyle(90)} className="limited-text">
                                                                                {job.metroName}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.tech &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <div style={getWidthStyle(90)} className="limited-text">
                                                                                {job.tech?.fullName}
                                                                            </div>
                                                                        </td>
                                                                    }
                                                                    {
                                                                        jobsColsVisibility?.createdDate &&
                                                                        <td style={getWidthStyle(120)}>
                                                                            <div style={getWidthStyle(115)} className="limited-text">
                                                                                {toPrettyDate(job.createdAt, selectedTimeZone?.id == "job" ? job.timezoneOffset : selectedTimeZone?.offset)}
                                                                            </div>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.lastUpdate &&
                                                                        <td style={getWidthStyle(120)}>
                                                                            <div style={getWidthStyle(115)} className="limited-text">
                                                                                {toPrettyDate(job.lastStatusUpdatedAt, selectedTimeZone?.id == "job" ? job.timezoneOffset : selectedTimeZone?.offset)}
                                                                            </div>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.apptDate &&
                                                                        <td style={getWidthStyle(120)}>
                                                                            <div style={getWidthStyle(115)} className="limited-text">
                                                                                {toPrettyDate(job.appointmentFrom, selectedTimeZone?.id == "job" ? job.timezoneOffset : selectedTimeZone?.offset)}
                                                                            </div>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.cancelNote &&
                                                                        <td style={getWidthStyle(200)}>
                                                                            <div>
                                                                                {job.lastStatusNote}
                                                                            </div>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.invNo &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <div>
                                                                                {
                                                                                    job.payment ?
                                                                                        <>{job.payment.externalInvoiceNumber}</>
                                                                                        : <>-</>
                                                                                }
                                                                            </div>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.total &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.payment ?
                                                                                    <>{moneyFormatter.format(job.payment.totalPaymentWithTax ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.cash &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>{moneyFormatter.format(job.paymentDetails.totalCash ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.ccmanual &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>{moneyFormatter.format(job.paymentDetails.totalCCManual ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.cccharge &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>{moneyFormatter.format(job.paymentDetails.totalCCCharge ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.cccharge &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>
                                                                                            {moneyFormatter.format(job.paymentDetails.totalCCChargeRate ?? 0)}
                                                                                        </div>
                                                                                        <div className="fs-9">
                                                                                            ({job.paymentDetails.ccRate}%)
                                                                                        </div>
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.check &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>{moneyFormatter.format(job.paymentDetails.totalCheck ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.check &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>
                                                                                            {moneyFormatter.format(job.paymentDetails.totalCheckRate ?? 0)}
                                                                                        </div>
                                                                                        <div className="fs-9">
                                                                                            ({job.paymentDetails.checkRate ?? 0}%)
                                                                                        </div>
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.epay &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails?.ePayItems && job.paymentDetails.ePayItems.length > 0 ?
                                                                                    <>
                                                                                        {
                                                                                            job.paymentDetails.ePayItems?.map((e, k) =>
                                                                                                <div key={`job-epay-item-${job.id}-${k}`} className="mb-1">
                                                                                                    <div>
                                                                                                        {moneyFormatter.format(e.amount ?? 0)}
                                                                                                    </div>
                                                                                                    <div className="text-muted fs-9">
                                                                                                        {e.name}
                                                                                                    </div>
                                                                                                    {
                                                                                                        (e.fee ?? 0) > 0 &&
                                                                                                        <div className="text-muted fs-9">
                                                                                                            Fee {moneyFormatter.format(e.fee ?? 0)} ({e.rate ?? 0}%)
                                                                                                        </div>
                                                                                                    }
                                                                                                </div>
                                                                                            )
                                                                                        }
                                                                                    </>
                                                                                    : <>{moneyFormatter.format(0)}</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.rinvoxPay &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {moneyFormatter.format(job.paymentDetails?.totalRinvoxPay ?? 0)}
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.rinvoxPay &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>
                                                                                            {moneyFormatter.format(job.paymentDetails.totalRinvoxFee ?? 0)}
                                                                                        </div>
                                                                                        <div className="fs-9">
                                                                                            ({job.paymentDetails.rinvoxRate ?? 0}%)
                                                                                        </div>
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.techParts &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>{moneyFormatter.format(job.paymentDetails.techParts ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.coParts &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>{moneyFormatter.format(job.paymentDetails.companyParts ?? 0)}</>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.tax &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>
                                                                                            {moneyFormatter.format(job.paymentDetails.totalTaxRate ?? 0)}
                                                                                        </div>
                                                                                        {
                                                                                            job.payment?.taxItems && job.payment?.taxItems.length > 0 &&
                                                                                            <FlexRow>
                                                                                                <span className="me-1 limited-text fs-9"
                                                                                                    style={{
                                                                                                        maxWidth: 40
                                                                                                    }}>
                                                                                                    {job.payment.taxItems[0].name}
                                                                                                </span>
                                                                                                <span className="fs-9">
                                                                                                    ({job.payment.taxItems[0].amountText})
                                                                                                </span>
                                                                                            </FlexRow>
                                                                                        }
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.techProfits &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>
                                                                                            {moneyFormatter.format(job.paymentDetails.techProfit ?? 0)}
                                                                                        </div>
                                                                                        <div className="fs-9">
                                                                                            ({job.paymentDetails.techRate ?? 0}%)
                                                                                        </div>
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.sourceProfits &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>{moneyFormatter.format(job.paymentDetails.sourceProfit ?? 0)}</div>
                                                                                        <div className="fs-9">
                                                                                            ({job.paymentDetails.sourceRate ?? 0}%)
                                                                                        </div>
                                                                                        {
                                                                                            job.sourceSetteled && (job.paymentDetails.sourceProfit ?? 0) > 0 &&
                                                                                            <div className="badge badge-light-primary badge-sm">
                                                                                                Settled
                                                                                            </div>
                                                                                        }
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.coProfits &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div>
                                                                                            {moneyFormatter.format(job.paymentDetails.companyProfit ?? 0)}
                                                                                        </div>
                                                                                        <div className="fs-9">
                                                                                            ({job.paymentDetails.companyRate ?? 0}%)
                                                                                        </div>
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.techFee &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            <>{moneyFormatter.format((job.paymentDetails?.techFee ?? 0) + (job.paymentDetails?.sourceFee ?? 0))}</>
                                                                        </td>
                                                                    }

                                                                    {
                                                                        jobsColsVisibility?.techBalance &&
                                                                        <td style={getWidthStyle(100)}>
                                                                            {
                                                                                job.paymentDetails ?
                                                                                    <>
                                                                                        <div className={!job.paymentDetails ? "" : (job.paymentDetails?.techBalance ?? 0) < 0 ? "text-danger" : "text-success"}>
                                                                                            {!job.paymentDetails ? "" : (job.paymentDetails?.techBalance ?? 0) < 0 ? "" : "+"}
                                                                                            {moneyFormatter.format(job.paymentDetails.techBalance ?? 0)}
                                                                                        </div>
                                                                                        {
                                                                                            job.techSetteled &&
                                                                                            <div className="badge badge-light-primary badge-sm">
                                                                                                Settled
                                                                                            </div>
                                                                                        }
                                                                                    </>
                                                                                    : <>-</>
                                                                            }
                                                                        </td>
                                                                    }


                                                                </tr>
                                                            )
                                                        }

                                                        {
                                                            body.segment != JobsChartSegments.CanceledJobs &&
                                                            <tr className="fw-bolder reports-total-row">
                                                                <td></td>
                                                                {
                                                                    (body.segment == JobsChartSegments.Payments || body.segment == JobsChartSegments.JobSource || body.segment == JobsChartSegments.ClosedJobs) &&
                                                                    <>
                                                                        <td></td>
                                                                        <td></td>
                                                                    </>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.status && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.name && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.phone && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.type && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.source && <td></td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.address && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.metro && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.tech && <td></td>
                                                                }
                                                                {
                                                                    jobsColsVisibility?.createdDate && <td></td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.lastUpdate && <td></td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.apptDate && <td></td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.cancelNote && <td></td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.invNo && <td></td>
                                                                }

                                                                <td className="text-center">Total</td>

                                                                {
                                                                    jobsColsVisibility?.total &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.amount ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.cash &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalCash ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.ccmanual &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalCCManual ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.cccharge &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalCCCharge ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.cccharge &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalCCChargeRate ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.check &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalCheck ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.check &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalCheckRate ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.epay &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalEPay ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.rinvoxPay &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalRinvoxPay ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.rinvoxPay &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalRinvoxFee ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.techParts &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.techParts ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.coParts &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.companyParts ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.tax &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.totalTaxRate ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.techProfits &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.techProfit ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.sourceProfits &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.sourceProfit ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.coProfits &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.companyProfit ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.techFee &&
                                                                    <td>{moneyFormatter.format(jobsTotalRow?.techFee ?? 0)}</td>
                                                                }

                                                                {
                                                                    jobsColsVisibility?.techBalance &&
                                                                    <td>
                                                                        <div className={(jobsTotalRow?.techBalance ?? 0) < 0 ? "text-danger" : "text-success"}>
                                                                            {(jobsTotalRow?.techBalance ?? 0) < 0 ? "" : "+"}
                                                                            {moneyFormatter.format(jobsTotalRow?.techBalance ?? 0)}
                                                                        </div>
                                                                    </td>
                                                                }
                                                            </tr>
                                                        }
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                }

                                {
                                    ((!data || data.length == 0) && (!payments || payments.length == 0) && (!jobs || jobs.length == 0)) &&
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="card-px text-center pt-15 pb-15">
                                                <h2 className="fs-2x fw-bolder mb-0">
                                                    Nothing Found!
                                                </h2>
                                                <p className="text-gray-400 fs-4 fw-bold py-7">
                                                    No data available for current filter selection!
                                                </p>
                                            </div>
                                            <div className="text-center pb-15 px-5">
                                                <img src="/assets/media/illustrations/sketchy-1/5.png" alt="" className="mw-100 h-200px h-sm-325px" />
                                            </div>
                                        </div>
                                    </div>
                                }

                            </Div>

                        </div>

                    </div>

                    {
                        addSegment &&
                        <Modal id="modal-segments-items"
                            open visible
                            title={segmentTitle()}
                            onOpen={() => $("#txt-chart-segmentitem-search").focus()}
                            onClose={() => addSegmentSet(false)}>
                            <div>
                                <div className="mb-3 position-relative">
                                    <input id="txt-chart-segmentitem-search" type="text" autoComplete="one-time-code"
                                        className="form-control form-control-sm form-control-solid" placeholder="Search..."
                                        onChange={e => {
                                            if (searchTimout) clearTimeout(searchTimout);
                                            searchTimout = setTimeout(() => fetchSegmentData(e.target.value), 500);
                                        }} />
                                </div>
                                {
                                    segmentsData.length == 0 &&
                                    <div className="py-10 text-danger">
                                        No Record Found!
                                    </div>
                                }
                                <div className="scroll h-300px h-xxl-400px">
                                    {
                                        segmentsData.map((item, key) =>
                                            <label key={key} onClick={e => selectSegmentItem(item)} className={"btn btn-outline btn-outline-dashed btn-outline-default p-3 min-w-150px d-flex align-items-center mb-2" + (segmentSelected(item.value) ? " active" : "")}>
                                                {
                                                    body.segment == JobsChartSegments.Dispatchers || body.segment == JobsChartSegments.Technician ?
                                                        <Avatar avatarUrl={item.avatar} width={26} fullName={item.title} />
                                                        : <Icon icon={segmentIcon()} size={18} />
                                                }
                                                <div className="ms-3">
                                                    <div className="text-dark fw-bolder fs-7 text-start">{item.title}</div>
                                                    <div className="text-gray-500 fs-8 text-start">{item.text}</div>
                                                </div>
                                            </label>
                                        )
                                    }
                                </div>

                            </div>
                        </Modal>
                    }


                    {
                        reportIssueModal && selectedJob &&
                        <JobReportIssue
                            onClose={() => reportIssueModalSet(false)}
                            onUpdate={j => {
                                var arr = jobs ?? [];
                                for (var i = 0; i < arr.length; i++) {
                                    if (j.id == arr[i].id) {
                                        arr[i].hasIssue = j.hasIssue;
                                        arr[i].issueText = j.issueText;
                                        break;
                                    }
                                }
                                jobsSet(a => [...arr]);
                            }}
                            job={selectedJob} />
                    }

                    {
                        columnsDrawer && body &&
                        <JobsReportColumnsDrawer
                            body={body}
                            data={data} payments={payments} jobs={jobs}
                            dataColsVisibility={dataColsVisibility} dataColsVisibilitySet={dataColsVisibilitySet}
                            dataStatusCols={dataStatusCols} dataStatusColsSet={dataStatusColsSet}
                            dataColsCount={dataColsCount}
                            techColsVisibility={techColsVisibility} techColsVisibilitySet={techColsVisibilitySet}
                            techColsCount={techColsCount}
                            jobsColsVisibility={jobsColsVisibility} jobsColsVisibilitySet={jobsColsVisibilitySet}
                            jobsColsCount={jobsColsCount}
                            dates={dates}
                            timezones={timezoneOptions()}
                            onApply={patch => {
                                if (!bodyRef.current) return;
                                saveSegmentBasis(bodyRef.current.segment, patch.dateType, patch.timezone);
                                setJobsReportBody({ ...bodyRef.current, ...patch });
                            }}
                            onCustomRange={customRangePicked}
                            onClose={() => columnsDrawerSet(false)} />
                    }

                    {
                        exportModal && body && sortBody &&
                        <JobReportSendDrawer
                            onClose={() => {
                                exportModalSet(false)
                                exportTechIdSet(undefined)
                                defaultSegmentIdSet(undefined)
                            }}
                            onSent={fetch} defaultSegmentId={defaultSegmentId}
                            techId={exportTechId}
                            jobVis={jobsColsVisibility} dataVis={dataColsVisibility} techVis={techColsVisibility}
                            body={body} sort={sortBody} query={filterQuery} />
                    }



                </>
            }
        </>
    )
}